/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import { FindLinkRegex } from "../../../../hook/useLinkPopover";
import { ITag } from "../../../../types/TagTypes";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import InterestTagSelect from "../../../TagSelect/InterestTagSelect";
import TagSelect from "../../../TagSelect/TagSelect";

const ContainerTooltip = styled(motion.div)`
  width: 550px;
  height: auto;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  margin: 0px -25px;

  background-color: white;

  * {
    font-family: "Source Sans Pro", sans-serif !important;
    font-size: 15px !important;
  }

  .css-u42o2z {
    margin-bottom: 16px !important;
  }

  > div {
    width: 100%;
  }
`;

const ButtonContainer = styled.div`
  margin-top: 25px;
  padding: 0 25px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const InputWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  .label-title {
    font: normal normal 600 16px/26px "Source Sans Pro" !important;
    color: #333333 !important;
    letter-spacing: 0 !important;
  }
`;

const LinkPopoverContent = styled.div`
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
  padding: 25px 25px 40px 25px;
  margin-top: 20px;
`;

const DisabledInputCss = css`
  background-color: #f7f7f7 !important;
  border-color: #cdcdcd !important;
  color: #3f3f3f !important;
  opacity: 1 !important;
`;

const variants = {
  visible: { opacity: 1, height: "auto" },
  hidden: { opacity: 0, height: 0 },
};

interface Props {
  selectedText?: string;
  onSave: (url: string, text: string, selectedTags: ITag[]) => any;
  onCancel: () => any;
  hasAnchor: boolean;
  isRawLink?: boolean;
  linkURL?: string;
  linkLabel?: string;
}

export default function LinkPopover({
  selectedText,
  onSave,
  onCancel,
  hasAnchor,
  linkURL,
  linkLabel,
  isRawLink,
}: Props) {
  const [selectedTags, setSelectedTags] = useState<ITag[]>([]);
  const [text, setText] = useState<string>("");
  const [url, setUrl] = useState<string>("");
  const [isLink, setIsLink] = useState<boolean>(false);
  const tagsPool = useRecoilValue(TagsAtom);

  useEffect(() => {
    if (hasAnchor) {
      setText(linkLabel);

      // Raw link
      if (isRawLink) {
        setUrl(linkLabel);
        setIsLink(true);
      } else {
        setUrl(linkURL);
        setIsLink(false);
      }
    } else {
      setText(selectedText);
      setIsLink(false);
    }
  }, [tagsPool]);

  return (
    <ContainerTooltip
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      transition={{ ease: "easeOut", duration: 0.1 }}
    >
      <LinkPopoverContent>
        {!isLink && (
          <InputWrapper>
            <div className="label-title">Text to display</div>

            <StyledInput
              css={DisabledInputCss}
              disabled={true}
              value={text}
              onChange={() => {}}
            />
          </InputWrapper>
        )}

        <InputWrapper
          css={css`
            margin: 16px 0;
            div:last-child > input {
              ${isLink && DisabledInputCss}
            }
            .css-awjbdj-InterestTagSelect {
              opacity: ${isLink && "1 !important"};
            }
          `}
        >
          <div className="label-title">URL</div>

          <InterestTagSelect
            interestTagging
            // links={interestTags}
            links={[]}
            tagInputValue={decodeURIComponent(url)}
            setTagInputValue={setUrl}
            placeholder={"Type or select url"}
            disabled={isLink}
            // handleChoseLinks={setInterestTags}
            handleChoseLinks={() => {}}
          />
        </InputWrapper>
        {/* 
        <InputWrapper
          css={css`
            > div {
              width: 100%;
            }

            // Should be removed!
            .e1sv7fj11 {
              height: 40px !important;
            }
          `}
        >
          <div className="label-title">Apply tag(s)</div>

          
          <TagSelect
            tags={selectedTags}
            preserveSystemTags={true}
            handleChoseTags={setSelectedTags}
          />
        </InputWrapper> */}
      </LinkPopoverContent>
      <ButtonContainer>
        <StyledButton
          css={css`
            height: 40px;
            width: 90px;
          `}
          className={"link"}
          inverted
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCancel();
          }}
        >
          Cancel
        </StyledButton>
        <StyledButton
          className={"link"}
          disabled={!url.trim() || !FindLinkRegex.test(url)}
          css={css`
            margin-left: 16px;
            height: 40px;
            width: 90px;
          `}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!url.trim() || !FindLinkRegex.test(url)) {
              toast("Please, provide a valid link text anchor and URL", {
                type: "info",
              });
              return;
            }

            onSave(url, text, selectedTags);
          }}
        >
          Save
        </StyledButton>
      </ButtonContainer>
    </ContainerTooltip>
  );
}
